// src/modules/pos/components/TicketLinker.tsx

import React, { useState, useRef } from "react";
import type { RepairTicket } from "../types/pos";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
// import { useNavigate } from "react-router-dom";
import { TextField, Button } from "@mui/material";

type Props = {
  value: RepairTicket | null;
  onChange: (ticket: RepairTicket | null) => void;
};

const TicketLinker: React.FC<Props> = ({ value, onChange }) => {
  const { token } = useAuth();
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<RepairTicket[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<RepairTicket | null>(value);
  const [error, setError] = useState<string | null>(null);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);


  // const navigate = useNavigate();

  // Live search
  const doSearch = async (q: string) => {
    setError(null);
    if (!q.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get("/tickets/search", {
        params: { q },
        headers: { Authorization: `Bearer ${token}` },
      });
      setResults(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError("Failed to search tickets");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Debounce search input
  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setSearch(v);
    setError(null);
    clearTimeout(searchTimeout.current as any);
    if (v.trim().length === 0) {
      setResults([]);
      return;
    }
    searchTimeout.current = setTimeout(() => doSearch(v), 350);
  };

  // Select a ticket from search results
  const handleSelect = (ticket: RepairTicket) => {
    setSelected(ticket);
    onChange(ticket);
    setResults([]);
    setSearch(ticket.ticketNumber || ticket.clientName || "");
  };

  // Unlink/clear
  const clearSelected = () => {
    setSelected(null);
    setSearch("");
    setResults([]);
    onChange(null);
  };

  return (
    <div className="mb-4">
      <label className="block font-medium mb-1">Link to Repair Ticket:</label>
      <div className="flex gap-2">
        <TextField
          fullWidth
          placeholder="Search by ticket number or client name..."
          value={search}
          onChange={handleInput}
          disabled={!!selected}
          size="small"
        />
        <Button variant="outlined" size="small" onClick={clearSelected} disabled={!selected && !search}>
          Clear
        </Button>
      </div>
      {/* Results dropdown */}
      {loading && <div className="text-xs text-gray-400 mt-1">Searching...</div>}
      {error && <div className="text-xs text-red-500 mt-1">{error}</div>}
      {results.length > 0 && !selected && (
        <div className="bg-white border mt-1 rounded shadow max-h-56 overflow-auto z-40 relative">
          {results.map((t) => (
            <button
              key={t._id}
              className="block w-full text-left px-3 py-2 hover:bg-blue-50 border-b last:border-0"
              onClick={() => handleSelect(t)}
              type="button"
            >
              <span className="font-bold text-blue-700">
                {t.ticketNumber}
              </span>{" "}
              — <span className="text-gray-700">{t.clientName}</span>
              <span className="block text-xs text-gray-500">
                Status: {t.status}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Show selected ticket details */}
      {selected && (
  <div className="mt-2 bg-blue-50 border border-blue-300 rounded p-3">
    <div className="font-bold text-blue-900 mb-1 flex justify-between items-center">
      Ticket: {selected.ticketNumber}
      <button
        onClick={clearSelected}
        className="ml-2 text-xs bg-red-200 hover:bg-red-400 rounded px-2 py-0.5 text-red-700"
        type="button"
      >
        Unlink
      </button>
    </div>
    <div>
      <span className="font-semibold">Client:</span> {selected.clientName}
    </div>
    <div>
      <span className="font-semibold">Status:</span> {selected.status}
    </div>
    <div>
      <a
        href={`/tickets/${selected._id}`}
        className="underline text-blue-700 hover:text-blue-900 mt-2 inline-block"
        target="_blank"
        rel="noopener noreferrer"
      >
        View Full Ticket Details &rarr;
      </a>
    </div>
  </div>
)}

    </div>
  );
};

export default TicketLinker;
